import {
  CloudDownloadOutlined,
  EyeOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileDoneOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FolderOpenOutlined,
  AudioOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Avatar,
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  type TableColumnsType,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";

import { ApiError } from "../api/client";
import {
  createAuditExport,
  downloadAuditExport,
  fetchAuditEvents,
  fetchAuditExports,
  fetchAuditSummary,
  type AuditCategory,
  type AuditEvent,
  type AuditExportJob,
  type AuditFileType,
  type AuditResult,
} from "../api/audit";
import type { UserRole, UserStatus } from "../api/auth";

const { RangePicker } = DatePicker;

const RESULT_META: Record<AuditResult, { label: string; color: string }> = {
  SUCCEEDED: { label: "成功", color: "success" },
  FAILED: { label: "失败", color: "error" },
  DENIED: { label: "拒绝", color: "error" },
};

const ROLE_META: Record<UserRole, { label: string; color: string }> = {
  ADMIN: { label: "管理员", color: "blue" },
  REVIEWER: { label: "审核员", color: "purple" },
  NORMAL_USER: { label: "普通用户", color: "default" },
};

const STATUS_META: Record<UserStatus, { label: string; color: string }> = {
  ACTIVE: { label: "正常", color: "success" },
  DISABLED: { label: "已禁用", color: "error" },
};

const FILE_TYPE_OPTIONS: Array<{ value: AuditFileType; label: string }> = [
  { value: "IMAGE", label: "图片" },
  { value: "PDF", label: "PDF" },
  { value: "DOCUMENT", label: "文档" },
  { value: "VIDEO", label: "视频" },
  { value: "AUDIO", label: "音频" },
  { value: "OTHER", label: "其他" },
];

const EVENT_META: Record<string, { label: string; color: string }> = {
  LOGIN_SUCCEEDED: { label: "登录成功", color: "blue" },
  LOGIN_FAILED: { label: "登录失败", color: "red" },
  LOGOUT_SUCCEEDED: { label: "退出登录", color: "default" },
  PASSWORD_CHANGED: { label: "修改密码", color: "purple" },
  PASSWORD_CHANGE_FAILED: { label: "修改密码失败", color: "red" },
  CSRF_VALIDATION_FAILED: { label: "会话校验失败", color: "red" },
  PROJECT_CREATED: { label: "创建项目", color: "blue" },
  PROJECT_UPDATED: { label: "修改项目", color: "purple" },
  PROJECT_DELETED: { label: "删除项目", color: "red" },
  PROJECT_RESTORED: { label: "恢复项目", color: "green" },
  PROJECT_STATUS_UPDATED: { label: "更新状态", color: "cyan" },
  PROJECT_VIEW_DELIVERED: { label: "查看项目", color: "default" },
  PROJECT_VIEW_FAILED: { label: "查看项目失败", color: "red" },
  PROJECT_UPDATE_CONFLICT: { label: "修改冲突", color: "orange" },
  PROJECT_STATUS_CONFLICT: { label: "状态冲突", color: "orange" },
  PROJECT_BATCH_STATUS_CONFLICT: { label: "批量处理冲突", color: "orange" },
  PROJECT_VALIDATION_FAILED: { label: "项目校验失败", color: "red" },
  PROJECT_BASIC_IMPORT_VALIDATED: { label: "基础信息导入校验", color: "cyan" },
  PROJECT_BASIC_IMPORT_VALIDATION_FAILED: { label: "基础信息校验失败", color: "red" },
  PROJECT_IMPORT_VALIDATED: { label: "项目导入校验", color: "cyan" },
  PROJECT_IMPORT_VALIDATION_FAILED: { label: "项目导入校验失败", color: "red" },
  PROJECT_IMPORT_CONFIRMED: { label: "确认项目导入", color: "blue" },
  PROJECT_IMPORT_CONFIRM_REPLAYED: { label: "重复确认导入", color: "orange" },
  PROJECT_IMPORTED: { label: "导入项目", color: "blue" },
  PROJECT_IMPORT_ERROR_REPORT_DOWNLOADED: { label: "下载导入错误报告", color: "geekblue" },
  PROJECT_CLOUD_DRIVE_INFO_VIEWED: { label: "查看网盘信息", color: "geekblue" },
  PROJECT_CLOUD_DRIVE_INFO_COPIED: { label: "复制网盘信息", color: "geekblue" },
  REVIEW_ASSIGNMENT_CREATED: { label: "分配审核员", color: "blue" },
  REVIEW_ASSIGNMENT_REASSIGNED: { label: "重新分配", color: "purple" },
  REVIEW_ASSIGNMENT_REVOKED: { label: "撤销审核", color: "orange" },
  REVIEW_ASSIGNMENT_DUPLICATE_IGNORED: { label: "忽略重复分配", color: "default" },
  REVIEW_TASK_VIEW_DELIVERED: { label: "查看审核任务", color: "default" },
  REVIEW_TASK_VIEW_DENIED: { label: "越权查看任务", color: "red" },
  REVIEW_TASK_CLOUD_DRIVE_INFO_VIEWED: { label: "审核查看网盘信息", color: "geekblue" },
  REVIEW_TASK_CLOUD_DRIVE_INFO_COPIED: { label: "审核复制网盘信息", color: "geekblue" },
  REVIEW_TASK_CLOUD_DRIVE_INFO_COPY_FAILED: { label: "复制网盘信息失败", color: "red" },
  REGISTRANT_CREATED: { label: "新增登记人", color: "blue" },
  REGISTRANT_UPDATED: { label: "修改登记人", color: "purple" },
  REGISTRANT_DELETED: { label: "删除登记人", color: "red" },
  REGISTRANT_VIEWED: { label: "查看登记人", color: "default" },
  REGISTRANT_SENSITIVE_VIEWED: { label: "查看登记人敏感信息", color: "purple" },
  REGISTRANT_SENSITIVE_VIEW_DENIED: { label: "敏感信息访问被拒", color: "red" },
  REGISTRANT_ACCESS_DENIED: { label: "登记人访问被拒", color: "red" },
  REGISTRANT_UPDATE_CONFLICT: { label: "登记人修改冲突", color: "orange" },
  ATTACHMENT_UPLOAD_INTENT_CREATED: { label: "创建上传任务", color: "cyan" },
  ATTACHMENT_UPLOAD_INTENT_EXPIRED: { label: "上传任务已过期", color: "default" },
  ATTACHMENT_UPLOAD_COMPLETED: { label: "上传附件", color: "blue" },
  ATTACHMENT_UPLOAD_CONTENT_REJECTED: { label: "附件内容被拒绝", color: "red" },
  ATTACHMENT_VALIDATION_PASSED: { label: "附件校验通过", color: "success" },
  ATTACHMENT_VALIDATION_REJECTED: { label: "附件校验未通过", color: "red" },
  ATTACHMENT_SCAN_PASSED: { label: "安全扫描通过", color: "success" },
  ATTACHMENT_OBJECT_PURGED: { label: "清理附件文件", color: "default" },
  ATTACHMENT_OBJECT_PURGE_FAILED: { label: "附件清理失败", color: "red" },
  ATTACHMENT_ACCESS_FAILED: { label: "附件访问失败", color: "red" },
  ATTACHMENT_RENAMED: { label: "重命名附件", color: "orange" },
  ATTACHMENT_DELETED: { label: "删除附件", color: "red" },
  ATTACHMENT_PREVIEW_AUTHORIZED: { label: "申请查看附件", color: "purple" },
  ATTACHMENT_PREVIEW_DELIVERED: { label: "查看附件", color: "purple" },
  ATTACHMENT_DOWNLOAD_AUTHORIZED: { label: "申请下载附件", color: "geekblue" },
  ATTACHMENT_DOWNLOAD_DELIVERED: { label: "下载附件", color: "geekblue" },
  ATTACHMENT_PREVIEW_AUTHORIZATION_FAILED: { label: "查看附件失败", color: "red" },
  ATTACHMENT_DOWNLOAD_AUTHORIZATION_FAILED: { label: "下载附件失败", color: "red" },
  ATTACHMENT_PREVIEW_DELIVERY_FAILED: { label: "附件预览送达失败", color: "red" },
  ATTACHMENT_DOWNLOAD_DELIVERY_FAILED: { label: "附件下载送达失败", color: "red" },
  ATTACHMENT_UPLOAD_INTENT_REJECTED: { label: "上传被拒绝", color: "red" },
  ATTACHMENT_UPLOAD_COMPLETE_FAILED: { label: "上传失败", color: "red" },
  USER_CREATED: { label: "创建用户", color: "blue" },
  USER_UPDATED: { label: "修改用户资料", color: "default" },
  USER_PASSWORD_RESET: { label: "重置密码", color: "purple" },
  AGENT_PROFILE_UPDATED: { label: "修改经办人", color: "orange" },
  BOOTSTRAP_ADMIN_CREATED: { label: "初始化管理员", color: "blue" },
  CORE_ADMIN_INITIALIZED: { label: "初始化核心管理员", color: "blue" },
  NON_CORE_USERS_CLEANED: { label: "清理非核心账号", color: "orange" },
  RBAC_ACCESS_DENIED: { label: "越权访问", color: "red" },
  AUDIT_EXPORT_REQUESTED: { label: "导出审计日志", color: "geekblue" },
  AUDIT_EXPORT_DOWNLOAD_AUTHORIZED: { label: "下载审计日志", color: "geekblue" },
  AUDIT_EXPORT_READY: { label: "审计导出已生成", color: "success" },
  AUDIT_EXPORT_FAILED: { label: "审计导出失败", color: "red" },
  AUDIT_EXPORT_OBJECT_DELIVERED: { label: "审计导出已送达", color: "geekblue" },
};

const CATEGORY_OPTIONS: Record<AuditCategory, string[]> = {
  LOGIN: ["LOGIN_SUCCEEDED", "LOGIN_FAILED"],
  PROJECT: [
    "PROJECT_CREATED",
    "PROJECT_UPDATED",
    "PROJECT_DELETED",
    "PROJECT_RESTORED",
    "PROJECT_STATUS_UPDATED",
    "PROJECT_VIEW_DELIVERED",
    "PROJECT_VIEW_FAILED",
    "PROJECT_UPDATE_CONFLICT",
    "PROJECT_STATUS_CONFLICT",
    "PROJECT_BATCH_STATUS_CONFLICT",
    "PROJECT_VALIDATION_FAILED",
    "PROJECT_BASIC_IMPORT_VALIDATED",
    "PROJECT_BASIC_IMPORT_VALIDATION_FAILED",
    "PROJECT_IMPORT_VALIDATED",
    "PROJECT_IMPORT_VALIDATION_FAILED",
    "PROJECT_IMPORT_CONFIRMED",
    "PROJECT_IMPORT_CONFIRM_REPLAYED",
    "PROJECT_IMPORTED",
    "PROJECT_IMPORT_ERROR_REPORT_DOWNLOADED",
    "PROJECT_CLOUD_DRIVE_INFO_VIEWED",
    "PROJECT_CLOUD_DRIVE_INFO_COPIED",
    "REVIEW_ASSIGNMENT_CREATED",
    "REVIEW_ASSIGNMENT_REASSIGNED",
    "REVIEW_ASSIGNMENT_REVOKED",
    "REVIEW_ASSIGNMENT_DUPLICATE_IGNORED",
    "REVIEW_TASK_VIEW_DELIVERED",
    "REVIEW_TASK_VIEW_DENIED",
    "REVIEW_TASK_CLOUD_DRIVE_INFO_VIEWED",
    "REVIEW_TASK_CLOUD_DRIVE_INFO_COPIED",
    "REVIEW_TASK_CLOUD_DRIVE_INFO_COPY_FAILED",
    "REGISTRANT_CREATED",
    "REGISTRANT_UPDATED",
    "REGISTRANT_DELETED",
    "REGISTRANT_VIEWED",
    "REGISTRANT_SENSITIVE_VIEWED",
    "REGISTRANT_SENSITIVE_VIEW_DENIED",
    "REGISTRANT_ACCESS_DENIED",
    "REGISTRANT_UPDATE_CONFLICT",
  ],
  ATTACHMENT: [
    "ATTACHMENT_UPLOAD_COMPLETED",
    "ATTACHMENT_UPLOAD_CONTENT_REJECTED",
    "ATTACHMENT_PREVIEW_AUTHORIZED",
    "ATTACHMENT_DOWNLOAD_AUTHORIZED",
    "ATTACHMENT_PREVIEW_AUTHORIZATION_FAILED",
    "ATTACHMENT_DOWNLOAD_AUTHORIZATION_FAILED",
    "ATTACHMENT_ACCESS_FAILED",
    "ATTACHMENT_RENAMED",
    "ATTACHMENT_DELETED",
    "ATTACHMENT_UPLOAD_INTENT_REJECTED",
    "ATTACHMENT_UPLOAD_COMPLETE_FAILED",
  ],
  USER: [
    "USER_CREATED",
    "USER_UPDATED",
    "USER_PASSWORD_RESET",
    "AGENT_PROFILE_UPDATED",
    "LOGOUT_SUCCEEDED",
    "PASSWORD_CHANGED",
    "PASSWORD_CHANGE_FAILED",
    "CSRF_VALIDATION_FAILED",
    "RBAC_ACCESS_DENIED",
    "AUDIT_EXPORT_REQUESTED",
    "AUDIT_EXPORT_DOWNLOAD_AUTHORIZED",
  ],
};

const SYSTEM_CATEGORY_OPTIONS: Record<AuditCategory, string[]> = {
  LOGIN: [],
  PROJECT: [],
  ATTACHMENT: [
    "ATTACHMENT_UPLOAD_INTENT_CREATED",
    "ATTACHMENT_UPLOAD_INTENT_EXPIRED",
    "ATTACHMENT_VALIDATION_PASSED",
    "ATTACHMENT_VALIDATION_REJECTED",
    "ATTACHMENT_SCAN_PASSED",
    "ATTACHMENT_OBJECT_PURGED",
    "ATTACHMENT_OBJECT_PURGE_FAILED",
    "ATTACHMENT_PREVIEW_DELIVERED",
    "ATTACHMENT_PREVIEW_DELIVERY_FAILED",
    "ATTACHMENT_DOWNLOAD_DELIVERED",
    "ATTACHMENT_DOWNLOAD_DELIVERY_FAILED",
  ],
  USER: ["AUDIT_EXPORT_READY", "AUDIT_EXPORT_FAILED", "AUDIT_EXPORT_OBJECT_DELIVERED"],
};

const CATEGORY_COPY: Record<
  AuditCategory,
  { label: string; search: string; operation: string; note: string }
> = {
  LOGIN: {
    label: "登录记录",
    search: "搜索姓名、账号、IP或事件类型",
    operation: "登录操作类型",
    note: "IP 按服务端识别的真实客户端地址记录",
  },
  PROJECT: {
    label: "项目操作",
    search: "搜索项目、操作人员、IP或事件类型",
    operation: "项目操作类型",
    note: "项目修改保留操作前后字段快照",
  },
  ATTACHMENT: {
    label: "附件操作",
    search: "搜索附件、项目、操作人员或IP",
    operation: "附件操作类型",
    note: "每个附件独立记录，并关联所属项目",
  },
  USER: {
    label: "用户与权限",
    search: "搜索操作人员、目标用户、IP或事件类型",
    operation: "用户操作类型",
    note: "权限变更保留修改前后快照",
  },
};

interface FilterValues {
  keyword: string;
  actorKeyword?: string;
  projectKeyword?: string;
  attachmentKeyword?: string;
  targetUserKeyword?: string;
  eventType?: string;
  actorRole?: UserRole;
  fileType?: AuditFileType;
  targetRole?: UserRole;
  targetStatus?: UserStatus;
  result?: AuditResult;
  createdFrom?: string;
  createdTo?: string;
  includeSystem: boolean;
}

const EMPTY_FILTERS: FilterValues = { keyword: "", includeSystem: false };

function payloadString(event: AuditEvent, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = event.payload?.[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
  }
  return undefined;
}

function formatTime(value: string) {
  return dayjs(value).format("YYYY-MM-DD HH:mm:ss");
}

function shortId(value?: string | null) {
  if (!value) return "-";
  return value.length > 14 ? `${value.slice(0, 8)}...${value.slice(-4)}` : value;
}

function eventMeta(eventType: string) {
  return (
    EVENT_META[eventType] ?? {
      label: eventType.replaceAll("_", " "),
      color: "default",
    }
  );
}

const FIELD_LABELS: Record<string, string> = {
  work_name: "作品名称",
  work_type: "作品类型",
  ownership_type: "权利归属方式",
  rights_ownership_type: "权利归属方式",
  creation_completed_date: "创作完成日期",
  publication_date: "创作发表日期",
  work_description: "作品简介",
  creation_process: "创作过程",
  cloud_drive_info: "网盘信息",
  status: "状态",
  version: "数据版本",
  name: "用户姓名",
  role: "用户角色",
  phone: "联系电话",
  username: "登录账号",
  registrant_type: "登记人类别",
  certificate_type: "证件类型",
  display_name: "附件名称",
  project_number: "项目编号",
};

const VALUE_LABELS: Record<string, string> = {
  NORMAL_USER: "普通用户",
  REVIEWER: "审核员",
  ADMIN: "管理员",
  ACTIVE: "正常",
  DISABLED: "已禁用",
  SUBMITTED: "已提交",
  COMPLETED: "已完成",
  UPLOADING: "上传中",
  QUARANTINED: "校验中",
  REJECTED: "已拒绝",
  BATCH: "批量操作",
  NATURAL_PERSON: "自然人",
  LEGAL_PERSON_OR_OTHER_ORGANIZATION: "法人或其他组织",
  LEGAL_ENTITY_WORK: "法人作品",
  NATURAL_PERSON_WORK: "自然人作品",
  COOPERATIVE_WORK: "合作作品",
  COMMISSIONED_WORK: "委托作品",
  EMPLOYMENT_WORK: "职务作品",
};

const REASON_LABELS: Record<string, string> = {
  INVALID_CREDENTIALS: "账号或密码错误",
  USER_DISABLED: "账号已被禁用",
  VERSION_CONFLICT: "数据版本冲突",
  CURRENT_PASSWORD_INVALID: "当前密码错误",
  FILE_TOO_LARGE: "附件超过大小限制",
  FILE_TYPE_NOT_ALLOWED: "不支持该附件类型",
  PREVIEW_NOT_SUPPORTED: "该文件不支持在线查看",
  CLOUD_DRIVE_INFO_EMPTY: "未填写网盘信息",
  ACCESS_DENIED: "没有访问权限",
  OWNER_NOT_ALLOWED: "不能操作其他用户的数据",
  ATTACHMENT_NOT_READY: "附件尚未完成处理",
  ATTACHMENT_NOT_FOUND: "附件不存在",
  PROJECT_NOT_FOUND: "项目不存在",
  USER_NOT_FOUND: "用户不存在",
  CSRF_VALIDATION_FAILED: "会话校验失败",
};

function displayValue(value: unknown) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string") return VALUE_LABELS[value] ?? value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value) ?? "-";
}

function describeChange(event: AuditEvent) {
  if (event.reason) return `原因：${REASON_LABELS[event.reason] ?? event.reason}`;
  const before = event.payload?.before;
  const after = event.payload?.after;
  if (typeof before === "string" || typeof after === "string") {
    return `${displayValue(before)} → ${displayValue(after)}`;
  }
  if (before && after && typeof before === "object" && typeof after === "object") {
    const beforeRecord = before as Record<string, unknown>;
    const afterRecord = after as Record<string, unknown>;
    const changed = Object.keys(afterRecord).filter(
      (key) => JSON.stringify(beforeRecord[key]) !== JSON.stringify(afterRecord[key]),
    );
    if (changed.length) {
      return changed
        .slice(0, 2)
        .map(
          (key) =>
            `${FIELD_LABELS[key] ?? key}：${displayValue(beforeRecord[key])} → ${displayValue(afterRecord[key])}`,
        )
        .join("；");
    }
  }
  const oldName = payloadString(event, "old_name", "before_name");
  const newName = payloadString(event, "new_name", "after_name");
  if (oldName || newName) return `${oldName ?? "-"} → ${newName ?? "-"}`;
  if (event.payload?.phone_changed === true) return "联系电话已修改";
  if (event.event_type.includes("CREATED") || event.event_type.includes("UPLOAD"))
    return "新增记录";
  if (event.event_type.includes("DELETED")) return "删除记录及关联关系";
  return "-";
}

function describeDevice(userAgent: string) {
  if (!userAgent || userAgent === "unknown") return "未知设备";
  const browser = userAgent.includes("Edg/")
    ? "Edge"
    : userAgent.includes("Chrome/")
      ? "Chrome"
      : userAgent.includes("Safari/")
        ? "Safari"
        : userAgent.includes("Firefox/")
          ? "Firefox"
          : "其他浏览器";
  const device = /Mobile|Android|iPhone|iPad/.test(userAgent) ? "移动端" : "桌面端";
  return `${device} · ${browser}`;
}

function formatBytes(value: number | null) {
  if (!value) return "-";
  if (value < 1024 * 1024) return `${Math.ceil(value / 1024)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function attachmentIcon(event: AuditEvent) {
  const mime = event.subject.attachment_content_type?.toLowerCase() ?? "";
  const name = event.subject.attachment_name?.toLowerCase() ?? "";
  if (mime.includes("pdf") || name.endsWith(".pdf")) {
    return <FilePdfOutlined className="audit-file-icon audit-file-icon--pdf" />;
  }
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/.test(name)) {
    return <FileImageOutlined className="audit-file-icon audit-file-icon--image" />;
  }
  if (mime.startsWith("video/") || /\.(mp4|mov|avi|mkv|webm)$/.test(name)) {
    return <VideoCameraOutlined className="audit-file-icon audit-file-icon--video" />;
  }
  if (mime.startsWith("audio/") || /\.(mp3|wav|aac|flac|m4a)$/.test(name)) {
    return <AudioOutlined className="audit-file-icon audit-file-icon--audio" />;
  }
  if (/\.(docx?|xlsx?|pptx?|txt)$/.test(name) || mime.includes("officedocument")) {
    return <FileWordOutlined className="audit-file-icon audit-file-icon--document" />;
  }
  return <FileTextOutlined className="audit-file-icon" />;
}

function attachmentTypeLabel(event: AuditEvent) {
  const mime = event.subject.attachment_content_type?.toLowerCase() ?? "";
  const name = event.subject.attachment_name?.toLowerCase() ?? "";
  if (mime.includes("pdf") || name.endsWith(".pdf")) return "PDF";
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp)$/.test(name)) return "图片";
  if (mime.startsWith("video/") || /\.(mp4|mov|avi|mkv|webm)$/.test(name)) return "视频";
  if (mime.startsWith("audio/") || /\.(mp3|wav|aac|flac|m4a)$/.test(name)) return "音频";
  if (/\.(docx?|xlsx?|pptx?|txt)$/.test(name) || mime.includes("officedocument")) {
    return "文档";
  }
  return mime || "其他文件";
}

function networkLocation(sourceIp: string) {
  if (sourceIp === "127.0.0.1" || sourceIp === "::1" || sourceIp === "localhost") {
    return "本机访问";
  }
  if (
    sourceIp.startsWith("10.") ||
    sourceIp.startsWith("192.168.") ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(sourceIp)
  ) {
    return "内网访问";
  }
  if (["worker", "cli", "internal"].includes(sourceIp)) return "系统内部";
  return "公网访问";
}

function ActorCell({ event }: { event: AuditEvent }) {
  const name =
    event.actor_kind === "SYSTEM"
      ? "系统任务"
      : (event.actor_name ?? event.actor_username ?? "未知账号");
  return (
    <div className="audit-identity-cell">
      <Avatar size={30}>{name.slice(0, 1)}</Avatar>
      <span>
        <strong>{name}</strong>
        {event.actor_name && event.actor_username ? <small>{event.actor_username}</small> : null}
      </span>
    </div>
  );
}

function RoleTag({
  role,
  actorKind,
}: {
  role: UserRole | null;
  actorKind: AuditEvent["actor_kind"];
}) {
  if (actorKind === "SYSTEM") return <Tag color="cyan">系统</Tag>;
  if (!role) return <span>-</span>;
  return <Tag color={ROLE_META[role].color}>{ROLE_META[role].label}</Tag>;
}

function OperationTag({ eventType }: { eventType: string }) {
  const meta = eventMeta(eventType);
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

function ResultTag({ result }: { result: AuditResult }) {
  const meta = RESULT_META[result];
  return <Tag color={meta.color}>{meta.label}</Tag>;
}

function AuditStatCard({
  tone,
  icon,
  title,
  value,
  detail,
}: {
  tone: "blue" | "indigo" | "purple" | "red";
  icon: React.ReactNode;
  title: string;
  value?: number;
  detail: React.ReactNode;
}) {
  return (
    <div className={`audit-stat-card audit-stat-card--${tone}`}>
      <div className="audit-stat-icon">{icon}</div>
      <div>
        <Statistic title={title} value={value ?? 0} />
        <div className="audit-stat-detail">{detail}</div>
      </div>
    </div>
  );
}

export function AuditEventsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<AuditCategory>("LOGIN");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [draft, setDraft] = useState<FilterValues>(EMPTY_FILTERS);
  const [dateDraft, setDateDraft] = useState<[Dayjs, Dayjs] | null>(null);
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);

  const summaryQuery = useQuery({
    queryKey: ["audit-summary"],
    queryFn: ({ signal }) => fetchAuditSummary(signal),
  });
  const query = useQuery({
    queryKey: ["audit-events", category, page, pageSize, filters],
    queryFn: ({ signal }) =>
      fetchAuditEvents(
        {
          page,
          pageSize,
          category,
          ...filters,
          includeSystem: filters.includeSystem,
          includeTestData: false,
        },
        signal,
      ),
  });
  const exportsQuery = useQuery({
    queryKey: ["audit-exports"],
    queryFn: ({ signal }) => fetchAuditExports(signal),
    enabled: exportsOpen,
    refetchInterval: (state) =>
      state.state.data?.items.some((item) => ["PENDING", "PROCESSING"].includes(item.status))
        ? 1500
        : false,
  });
  const exportMutation = useMutation({
    mutationFn: () =>
      createAuditExport({
        category,
        ...(filters.keyword ? { keyword: filters.keyword } : {}),
        ...(filters.actorKeyword ? { actor_keyword: filters.actorKeyword } : {}),
        ...(filters.projectKeyword ? { project_keyword: filters.projectKeyword } : {}),
        ...(filters.attachmentKeyword ? { attachment_keyword: filters.attachmentKeyword } : {}),
        ...(filters.targetUserKeyword ? { target_user_keyword: filters.targetUserKeyword } : {}),
        ...(filters.eventType ? { event_type: filters.eventType } : {}),
        ...(filters.actorRole ? { actor_role: filters.actorRole } : {}),
        ...(filters.fileType ? { file_type: filters.fileType } : {}),
        ...(filters.targetRole ? { target_role: filters.targetRole } : {}),
        ...(filters.targetStatus ? { target_status: filters.targetStatus } : {}),
        ...(filters.result ? { result: filters.result } : {}),
        ...(filters.createdFrom ? { created_from: filters.createdFrom } : {}),
        ...(filters.createdTo ? { created_to: filters.createdTo } : {}),
        include_system: filters.includeSystem,
        include_test_data: false,
      }),
    onSuccess: async () => {
      message.success("日志导出任务已创建");
      setExportsOpen(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["audit-exports"] }),
        queryClient.invalidateQueries({ queryKey: ["audit-events"] }),
        queryClient.invalidateQueries({ queryKey: ["audit-summary"] }),
      ]);
    },
    onError: (error) =>
      message.error(error instanceof ApiError ? error.message : "日志导出创建失败"),
  });

  const saveExport = async (id: string) => {
    try {
      const blob = await downloadAuditExport(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-export-${id.slice(0, 8)}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : "导出包下载失败");
    }
  };

  const columns = useMemo<TableColumnsType<AuditEvent>>(() => {
    const detailColumn = {
      title: "详情",
      key: "detail",
      width: 64,
      fixed: "right" as const,
      render: (_: unknown, event: AuditEvent) => (
        <Button type="link" size="small" onClick={() => setSelected(event)}>
          查看
        </Button>
      ),
    };
    const timeColumn = {
      title: "操作时间",
      dataIndex: "created_at",
      width: 168,
      render: (value: string) => formatTime(value),
    };
    const actorColumn = {
      title: "操作人员",
      key: "actor",
      width: 168,
      render: (_: unknown, event: AuditEvent) => <ActorCell event={event} />,
    };
    const roleColumn = {
      title: category === "USER" ? "操作人角色" : "角色",
      dataIndex: "actor_role",
      width: 110,
      render: (role: UserRole | null, event: AuditEvent) => (
        <RoleTag role={role} actorKind={event.actor_kind} />
      ),
    };
    const resultColumn = {
      title: "操作结果",
      dataIndex: "result",
      width: 92,
      render: (value: AuditResult) => <ResultTag result={value} />,
    };
    const ipColumn = { title: "访问IP", dataIndex: "source_ip", width: 132 };

    if (category === "LOGIN") {
      return [
        timeColumn,
        { ...actorColumn, title: "用户信息", width: 190 },
        roleColumn,
        ipColumn,
        {
          title: "登录地点",
          dataIndex: "source_ip",
          width: 104,
          render: (value: string) => networkLocation(value),
        },
        {
          title: "设备与浏览器",
          dataIndex: "user_agent",
          width: 150,
          ellipsis: true,
          render: (value: string) => <span title={value}>{describeDevice(value)}</span>,
        },
        {
          title: "登录方式",
          dataIndex: "event_type",
          width: 116,
          render: (value: string) =>
            value.startsWith("LOGIN_") ? "账号密码" : eventMeta(value).label,
        },
        resultColumn,
        detailColumn,
      ];
    }

    if (category === "ATTACHMENT") {
      return [
        timeColumn,
        actorColumn,
        roleColumn,
        {
          title: "附件操作",
          dataIndex: "event_type",
          width: 128,
          render: (value: string) => <OperationTag eventType={value} />,
        },
        {
          title: "附件信息",
          key: "attachment",
          width: 210,
          render: (_: unknown, event: AuditEvent) => {
            const name =
              event.subject.attachment_name ??
              payloadString(
                event,
                "display_name_snapshot",
                "original_name",
                "filename",
                "file_name",
              );
            return (
              <div className="audit-file-cell">
                {attachmentIcon(event)}
                <span>
                  <strong title={name}>{name ?? `附件 ${shortId(event.target_id)}`}</strong>
                  <small>
                    {[formatBytes(event.subject.attachment_size_bytes), attachmentTypeLabel(event)]
                      .filter((value) => value && value !== "-")
                      .join(" · ") || "附件记录"}
                  </small>
                </span>
              </div>
            );
          },
        },
        {
          title: "所属项目",
          key: "project",
          width: 170,
          ellipsis: true,
          render: (_: unknown, event: AuditEvent) =>
            event.subject.work_name ??
            event.subject.project_number ??
            event.subject.project_id ??
            "-",
        },
        {
          title: "变更内容",
          key: "change",
          width: 200,
          ellipsis: true,
          render: (_: unknown, event: AuditEvent) => describeChange(event),
        },
        ipColumn,
        resultColumn,
        detailColumn,
      ];
    }

    if (category === "USER") {
      return [
        timeColumn,
        actorColumn,
        roleColumn,
        {
          title: "用户操作",
          dataIndex: "event_type",
          width: 138,
          render: (value: string) => <OperationTag eventType={value} />,
        },
        {
          title: "目标用户",
          key: "target",
          width: 160,
          render: (_: unknown, event: AuditEvent) => (
            <div className="audit-target-cell">
              <strong>
                {event.subject.target_user_name ??
                  payloadString(event, "target_name", "name") ??
                  event.target_type ??
                  "-"}
              </strong>
              <small>{event.subject.target_user_phone_masked ?? shortId(event.target_id)}</small>
            </div>
          ),
        },
        {
          title: "变更内容",
          key: "change",
          ellipsis: true,
          render: (_: unknown, event: AuditEvent) => describeChange(event),
        },
        ipColumn,
        resultColumn,
        detailColumn,
      ];
    }

    return [
      timeColumn,
      actorColumn,
      roleColumn,
      {
        title: "项目操作",
        dataIndex: "event_type",
        width: 138,
        render: (value: string) => <OperationTag eventType={value} />,
      },
      {
        title: "项目信息",
        key: "project",
        width: 190,
        render: (_: unknown, event: AuditEvent) => (
          <div className="audit-target-cell">
            <strong>
              {event.subject.work_name ??
                event.subject.project_number ??
                payloadString(event, "work_name", "project_number") ??
                event.target_type ??
                "-"}
            </strong>
            <small>
              {event.subject.project_number ??
                payloadString(event, "project_number") ??
                shortId(event.target_id)}
            </small>
          </div>
        ),
      },
      {
        title: "变更内容",
        key: "change",
        ellipsis: true,
        render: (_: unknown, event: AuditEvent) => describeChange(event),
      },
      ipColumn,
      resultColumn,
      detailColumn,
    ];
  }, [category]);

  const applyFilters = () => {
    setFilters({
      ...draft,
      keyword: draft.keyword.trim(),
      actorKeyword: draft.actorKeyword?.trim() || undefined,
      projectKeyword: draft.projectKeyword?.trim() || undefined,
      attachmentKeyword: draft.attachmentKeyword?.trim() || undefined,
      targetUserKeyword: draft.targetUserKeyword?.trim() || undefined,
      createdFrom: dateDraft?.[0].startOf("day").toISOString(),
      createdTo: dateDraft?.[1].endOf("day").toISOString(),
    });
    setPage(1);
  };

  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setDateDraft(null);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const summary = summaryQuery.data;
  const auditRecordTotal = summary
    ? Object.values(summary.category_counts).reduce((total, count) => total + count, 0)
    : 0;
  const activeFilterLabels = [
    filters.keyword ? `关键词：${filters.keyword}` : "",
    filters.projectKeyword ? `项目：${filters.projectKeyword}` : "",
    filters.attachmentKeyword ? `附件：${filters.attachmentKeyword}` : "",
    filters.actorKeyword ? `操作人：${filters.actorKeyword}` : "",
    filters.targetUserKeyword ? `目标用户：${filters.targetUserKeyword}` : "",
    filters.eventType ? eventMeta(filters.eventType).label : "",
    filters.actorRole ? ROLE_META[filters.actorRole].label : "",
    filters.fileType
      ? FILE_TYPE_OPTIONS.find((item) => item.value === filters.fileType)?.label
      : "",
    filters.targetRole ? `目标角色：${ROLE_META[filters.targetRole].label}` : "",
    filters.targetStatus ? `账号状态：${STATUS_META[filters.targetStatus].label}` : "",
    filters.result ? RESULT_META[filters.result].label : "",
    filters.createdFrom && filters.createdTo
      ? `${dayjs(filters.createdFrom).format("YYYY-MM-DD")} 至 ${dayjs(filters.createdTo).format("YYYY-MM-DD")}`
      : "",
    filters.includeSystem ? "包含系统事件" : "",
  ].filter(Boolean);

  return (
    <main className="audit-page">
      <header className="audit-page-header">
        <div>
          <Typography.Title level={1}>审计日志</Typography.Title>
          <Typography.Paragraph>
            记录账号登录、项目操作及附件访问行为，保障材料安全可追溯
          </Typography.Paragraph>
        </div>
        <Space size={12}>
          <Button icon={<CloudDownloadOutlined />} onClick={() => setExportsOpen(true)}>
            导出日志
          </Button>
          <Button icon={<SafetyCertificateOutlined />} onClick={() => setPolicyOpen(true)}>
            审计策略
          </Button>
        </Space>
      </header>

      <section className="audit-stat-grid" aria-label="审计数据概览">
        <AuditStatCard
          tone="blue"
          icon={<UserOutlined />}
          title="今日登录账号"
          value={summary?.today_login_accounts}
          detail="重复登录仅计一次"
        />
        <AuditStatCard
          tone="indigo"
          icon={<FolderOpenOutlined />}
          title="今日项目增删"
          value={summary?.today_project_changes}
          detail={
            <>
              新增 {summary?.today_projects_created ?? 0} · 删除{" "}
              {summary?.today_projects_deleted ?? 0}
            </>
          }
        />
        <AuditStatCard
          tone="purple"
          icon={<FileDoneOutlined />}
          title="今日附件访问"
          value={summary?.today_unique_attachment_accesses}
          detail={
            <>
              查看 {summary?.today_unique_attachment_previews ?? 0} · 下载{" "}
              {summary?.today_unique_attachment_downloads ?? 0}
            </>
          }
        />
        <AuditStatCard
          tone="red"
          icon={<WarningOutlined />}
          title="累计风险事件"
          value={summary?.all_risk_events}
          detail="失败或越权访问"
        />
      </section>

      <section className="audit-record-panel">
        <div className="audit-record-heading">
          <Typography.Title level={2}>审计记录</Typography.Title>
          <span>共 {auditRecordTotal} 条</span>
        </div>
        <Tabs
          activeKey={category}
          onChange={(value) => {
            setCategory(value as AuditCategory);
            setDraft(EMPTY_FILTERS);
            setFilters(EMPTY_FILTERS);
            setDateDraft(null);
            setPage(1);
          }}
          items={(Object.keys(CATEGORY_COPY) as AuditCategory[]).map((key) => ({
            key,
            label: (
              <span>
                {CATEGORY_COPY[key].label}
                <small>{summary?.category_counts[key] ?? 0}</small>
              </span>
            ),
          }))}
        />

        <div className={`audit-filter-row audit-filter-row--${category.toLowerCase()}`}>
          {category === "LOGIN" ? (
            <Input
              className="audit-filter-wide"
              allowClear
              prefix={<FileSearchOutlined />}
              placeholder="搜索姓名、账号或访问 IP"
              value={draft.keyword}
              onChange={(event) =>
                setDraft((current) => ({ ...current, keyword: event.target.value }))
              }
              onPressEnter={applyFilters}
            />
          ) : null}
          {category === "PROJECT" ? (
            <>
              <Input
                className="audit-filter-wide"
                allowClear
                prefix={<FileSearchOutlined />}
                placeholder="搜索作品名称或项目编号"
                value={draft.projectKeyword}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    projectKeyword: event.target.value,
                  }))
                }
                onPressEnter={applyFilters}
              />
              <Input
                allowClear
                placeholder="操作人姓名或账号"
                value={draft.actorKeyword}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, actorKeyword: event.target.value }))
                }
                onPressEnter={applyFilters}
              />
            </>
          ) : null}
          {category === "ATTACHMENT" ? (
            <>
              <Input
                className="audit-filter-wide"
                allowClear
                prefix={<FileSearchOutlined />}
                placeholder="搜索附件名称"
                value={draft.attachmentKeyword}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    attachmentKeyword: event.target.value,
                  }))
                }
                onPressEnter={applyFilters}
              />
              <Input
                className="audit-filter-wide"
                allowClear
                placeholder="所属作品或项目编号"
                value={draft.projectKeyword}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    projectKeyword: event.target.value,
                  }))
                }
                onPressEnter={applyFilters}
              />
              <Input
                allowClear
                placeholder="操作人姓名或账号"
                value={draft.actorKeyword}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, actorKeyword: event.target.value }))
                }
                onPressEnter={applyFilters}
              />
            </>
          ) : null}
          {category === "USER" ? (
            <>
              <Input
                className="audit-filter-wide"
                allowClear
                prefix={<FileSearchOutlined />}
                placeholder="搜索目标用户姓名、账号或手机"
                value={draft.targetUserKeyword}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    targetUserKeyword: event.target.value,
                  }))
                }
                onPressEnter={applyFilters}
              />
              <Input
                allowClear
                placeholder="操作人姓名或账号"
                value={draft.actorKeyword}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, actorKeyword: event.target.value }))
                }
                onPressEnter={applyFilters}
              />
              <Input
                className="audit-filter-wide"
                allowClear
                placeholder="搜索访问 IP、请求编号或事件类型"
                value={draft.keyword}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, keyword: event.target.value }))
                }
                onPressEnter={applyFilters}
              />
            </>
          ) : null}
          <Select
            allowClear
            placeholder={CATEGORY_COPY[category].operation}
            value={draft.eventType}
            options={[
              ...CATEGORY_OPTIONS[category],
              ...(draft.includeSystem ? SYSTEM_CATEGORY_OPTIONS[category] : []),
            ].map((value) => ({ value, label: eventMeta(value).label }))}
            onChange={(value) => setDraft((current) => ({ ...current, eventType: value }))}
          />
          <Select<UserRole>
            allowClear
            placeholder="用户角色"
            value={draft.actorRole}
            options={(Object.keys(ROLE_META) as UserRole[]).map((value) => ({
              value,
              label: ROLE_META[value].label,
            }))}
            onChange={(value) => setDraft((current) => ({ ...current, actorRole: value }))}
          />
          {category === "ATTACHMENT" ? (
            <Select<AuditFileType>
              allowClear
              placeholder="文件类型"
              value={draft.fileType}
              options={FILE_TYPE_OPTIONS}
              onChange={(value) => setDraft((current) => ({ ...current, fileType: value }))}
            />
          ) : null}
          {category === "USER" ? (
            <>
              <Select<UserRole>
                allowClear
                placeholder="目标用户角色"
                value={draft.targetRole}
                options={(Object.keys(ROLE_META) as UserRole[]).map((value) => ({
                  value,
                  label: ROLE_META[value].label,
                }))}
                onChange={(value) => setDraft((current) => ({ ...current, targetRole: value }))}
              />
              <Select<UserStatus>
                allowClear
                placeholder="目标账号状态"
                value={draft.targetStatus}
                options={(Object.keys(STATUS_META) as UserStatus[]).map((value) => ({
                  value,
                  label: STATUS_META[value].label,
                }))}
                onChange={(value) => setDraft((current) => ({ ...current, targetStatus: value }))}
              />
            </>
          ) : null}
          <Select<AuditResult>
            allowClear
            placeholder="操作结果"
            value={draft.result}
            options={(Object.keys(RESULT_META) as AuditResult[]).map((value) => ({
              value,
              label: RESULT_META[value].label,
            }))}
            onChange={(value) => setDraft((current) => ({ ...current, result: value }))}
          />
          <RangePicker
            className="audit-filter-date"
            value={dateDraft}
            onChange={(value) => setDateDraft(value?.[0] && value[1] ? [value[0], value[1]] : null)}
            placeholder={["开始日期", "结束日期"]}
          />
          <Button onClick={resetFilters}>重置</Button>
          <Button type="primary" onClick={applyFilters}>
            查询
          </Button>
        </div>

        <div className="audit-filter-summary">
          <div>
            {activeFilterLabels.length ? (
              <>
                <strong>当前筛选</strong>
                {activeFilterLabels.map((label) => (
                  <Tag key={label}>{label}</Tag>
                ))}
                <Button type="link" size="small" onClick={resetFilters}>
                  清除全部
                </Button>
              </>
            ) : (
              <span />
            )}
          </div>
          <div className="audit-filter-summary-aside">
            <label className="audit-system-toggle">
              <Switch
                size="small"
                checked={filters.includeSystem}
                onChange={(checked) => {
                  setDraft((current) => ({ ...current, includeSystem: checked }));
                  setFilters((current) => ({ ...current, includeSystem: checked }));
                  setPage(1);
                }}
              />
              包含系统事件
            </label>
            <span>
              <SafetyCertificateOutlined /> {CATEGORY_COPY[category].note}
            </span>
          </div>
        </div>

        <Table<AuditEvent>
          className="audit-record-table"
          rowKey="id"
          size="middle"
          loading={query.isPending}
          dataSource={query.data?.items}
          columns={columns}
          scroll={{ x: category === "LOGIN" ? 1280 : 1160 }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无审计记录" />,
          }}
          footer={() => (
            <div className="audit-integrity-note">
              <span>
                <SafetyCertificateOutlined /> {CATEGORY_COPY[category].label}
                不可修改或删除，系统完整记录操作目标、人员、时间、真实 IP 与结果
              </span>
              <Button type="link" size="small" onClick={() => setPolicyOpen(true)}>
                查看审计规则
              </Button>
            </div>
          )}
          pagination={{
            current: page,
            pageSize,
            total: query.data?.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `共 ${total} 条`,
            onChange: (nextPage, nextSize) => {
              setPage(nextSize !== pageSize ? 1 : nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </section>

      <Drawer
        className="audit-detail-drawer"
        title="审计事件详情"
        size={680}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <>
            <div className="audit-detail-hero">
              <div className="audit-detail-icon">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <OperationTag eventType={selected.event_type} />
                <Typography.Title level={3}>
                  {eventMeta(selected.event_type).label}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {formatTime(selected.created_at)}
                </Typography.Text>
              </div>
              <ResultTag result={selected.result} />
            </div>
            <Descriptions
              bordered
              column={1}
              size="small"
              items={[
                {
                  key: "actor",
                  label: "操作人员",
                  children: `${selected.actor_name ?? "未知账号"}${selected.actor_username ? `（${selected.actor_username}）` : ""}`,
                },
                {
                  key: "role",
                  label: "用户角色",
                  children: selected.actor_role ? ROLE_META[selected.actor_role].label : "-",
                },
                {
                  key: "target",
                  label: "操作目标",
                  children: `${selected.target_type ?? "-"} · ${selected.target_id ?? "-"}`,
                },
                { key: "ip", label: "访问 IP", children: selected.source_ip },
                { key: "ua", label: "设备与浏览器", children: selected.user_agent },
                { key: "request", label: "请求 ID", children: selected.request_id },
                { key: "reason", label: "失败原因", children: selected.reason ?? "-" },
                {
                  key: "payload",
                  label: "数据快照",
                  children: (
                    <pre className="audit-payload">
                      {selected.payload ? JSON.stringify(selected.payload, null, 2) : "-"}
                    </pre>
                  ),
                },
              ]}
            />
          </>
        ) : null}
      </Drawer>

      <Modal
        title="审计策略"
        open={policyOpen}
        footer={
          <Button type="primary" onClick={() => setPolicyOpen(false)}>
            我知道了
          </Button>
        }
        onCancel={() => setPolicyOpen(false)}
      >
        <div className="audit-policy-list">
          <div>
            <SafetyCertificateOutlined />
            <span>
              <strong>权限隔离</strong>
              <small>操作日志仅管理员可查询和导出。</small>
            </span>
          </div>
          <div>
            <FileSearchOutlined />
            <span>
              <strong>完整留痕</strong>
              <small>记录操作人员、时间、真实 IP、请求 ID、目标及操作结果。</small>
            </span>
          </div>
          <div>
            <FileDoneOutlined />
            <span>
              <strong>保留周期</strong>
              <small>在线审计记录至少保留 2 年，临时导出文件保留 30 天。</small>
            </span>
          </div>
        </div>
      </Modal>

      <Modal
        title="导出日志"
        width={760}
        open={exportsOpen}
        onCancel={() => setExportsOpen(false)}
        footer={null}
      >
        <div className="audit-export-toolbar">
          <Typography.Text type="secondary">
            导出当前“{CATEGORY_COPY[category].label}”及已应用的筛选条件
          </Typography.Text>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            loading={exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
          >
            生成导出包
          </Button>
        </div>
        <Table<AuditExportJob>
          rowKey="id"
          size="small"
          loading={exportsQuery.isPending}
          dataSource={exportsQuery.data?.items}
          pagination={{ pageSize: 6, hideOnSinglePage: true }}
          columns={[
            { title: "创建时间", dataIndex: "created_at", render: formatTime },
            { title: "日志数量", dataIndex: "event_count", width: 90 },
            {
              title: "文件大小",
              dataIndex: "size_bytes",
              width: 90,
              render: formatBytes,
            },
            {
              title: "状态",
              dataIndex: "status",
              width: 100,
              render: (value: AuditExportJob["status"]) => (
                <Tag
                  color={
                    value === "READY" ? "success" : value === "FAILED" ? "error" : "processing"
                  }
                >
                  {value === "READY" ? "可下载" : value === "FAILED" ? "失败" : "生成中"}
                </Tag>
              ),
            },
            {
              title: "操作",
              width: 80,
              render: (_: unknown, record: AuditExportJob) => (
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  disabled={record.status !== "READY"}
                  onClick={() => void saveExport(record.id)}
                >
                  下载
                </Button>
              ),
            },
          ]}
        />
      </Modal>
    </main>
  );
}
